import { ReactComponent as Logo } from "../../vendor/logo_black.svg";
import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Alert as MuiAlert,
  TextField as MuiTextField,
  Paper,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Formik } from "formik";
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components/macro";
import * as Yup from "yup";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Brand = styled(Logo)`
  fill: ${(props) => props.theme.palette.primary.main};
  width: 250px;
  margin-bottom: 32px;
`;

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)};

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }
`;

const ResetPassword = () => {
  const token = useParams().token;
  return (
    <>
      <Brand />
      <Wrapper>
        <Helmet title="Reset Password" />

        <ResetPasswordForm token={token} />
      </Wrapper>
    </>
  );
};

const ResetPasswordForm = ({ token }) => {
  const RESET_PASSWORD_MUTATION = gql`
    mutation ResetPassword(
      $token: String!
      $newPassword1: String!
      $newPassword2: String!
    ) {
      passwordReset(
        token: $token
        newPassword1: $newPassword1
        newPassword2: $newPassword2
      ) {
        success
        errors
      }
    }
  `;

  const [resetPassword, { data, loading, error }] = useMutation(
    RESET_PASSWORD_MUTATION
  );

  const minPasswordLen = 8;

  return (
    <>
      {loading && (
        <Alert my={2} severity="info">
          Changing your password...
        </Alert>
      )}
      {(error || (data && !data["passwordReset"]["success"])) && (
        <Alert my={2} severity="warning">
          Sorry, there was an error. Please contact us at{" "}
          <a href="mailto:support@podium.live">support@podium.live</a>.
        </Alert>
      )}
      {data && data["passwordReset"]["success"] && (
        <Alert my={2} severity="success">
          Your password was successfully changed.
          <p>
            Please <Link to="/auth/sign-in">click here to sign in</Link>.
          </p>
        </Alert>
      )}
      {(!data || !data["passwordReset"]["success"]) && (
        <>
          <Typography component="h2" variant="body1" align="center">
            Enter a new password for your account
          </Typography>
          <Formik
            initialValues={{
              password: "",
              confirmPassword: "",
              submit: false,
            }}
            validationSchema={Yup.object().shape({
              password: Yup.string()
                .required("Required")
                .min(
                  minPasswordLen,
                  `Must be at least ${minPasswordLen} characters`
                )
                .max(255),
              confirmPassword: Yup.string()
                .required("Required")
                .when("password", {
                  is: (val) => val && val.length > 0,
                  then: Yup.string().oneOf(
                    [Yup.ref("password")],
                    "Both passwords must match"
                  ),
                }),
            })}
            onSubmit={async (
              values,
              { setErrors, setStatus, setSubmitting }
            ) => {
              try {
                await resetPassword({
                  variables: {
                    token: token,
                    newPassword1: values.password,
                    newPassword2: values.confirmPassword,
                  },
                });
              } catch (error) {
                const message = error.message || "Something went wrong";

                setStatus({ success: false });
                setErrors({ submit: message });
                setSubmitting(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
            }) => (
              <form noValidate onSubmit={handleSubmit}>
                {errors.submit && (
                  <Alert mt={2} mb={1} severity="warning">
                    {errors.submit}
                  </Alert>
                )}
                <TextField
                  type="password"
                  name="password"
                  label="Password"
                  value={values.password}
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={3}
                  required
                />
                <TextField
                  type="password"
                  name="confirmPassword"
                  label="Confirm password"
                  value={values.confirmPassword}
                  error={Boolean(
                    touched.confirmPassword && errors.confirmPassword
                  )}
                  fullWidth
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  my={3}
                  required
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  Reset password
                </Button>
                <Button
                  component={Link}
                  to="/auth/sign-in"
                  fullWidth
                  color="primary"
                >
                  Cancel
                </Button>
              </form>
            )}
          </Formik>
        </>
      )}
    </>
  );
};

export default ResetPassword;
